import { isTabletDevice } from '@va/dashboard/selectors/ui';
import { NotesIcon, SearchIconV2 } from '@va/icons';
import { useLocalizationContext, useTranslate } from '@va/localization';
import { fontWeights, Heading3, IconWithBackground, Paragraph, ParagraphWithTooltip } from '@va/ui/design-system';
import { usePrevious } from '@va/util/hooks';
import { useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { AiChatbotView } from '../../../types';
import { useAiChatbotContext } from '../../aiChatbotContext';
import { ChatInput } from '../../ChatInput';
import { ExampleQuestion } from '../../ExampleQuestion';
import { LimitReachedWarning } from '../../LimitReachedWarning';
import { ChatDetailsContextProvider, useChatDetailsContext } from './ChatDetailsContext';
import { ChatMessages } from './ChatMessages';
import { ChatOptionsButton } from './ChatOptionsButton';

export const ChatDetails = () => {
  return (
    <ChatDetailsContextProvider>
      <ChatDetailsContent />
    </ChatDetailsContextProvider>
  );
};

export const ChatDetailsContent = () => {
  const isTablet = useSelector(isTabletDevice);
  const { translate } = useLocalizationContext();
  const { conversation, isLoading, error, addMessage, isStreamingMessage, stopStreamingMessage } =
    useChatDetailsContext();
  const { selectedChat, selectedView } = useAiChatbotContext();
  const previousView = usePrevious(selectedView);

  useEffect(() => {
    if (selectedView === AiChatbotView.NewChat && previousView === AiChatbotView.ChatDetails && isStreamingMessage) {
      stopStreamingMessage();
    }
  }, [isStreamingMessage, previousView, selectedView, stopStreamingMessage]);

  if (error) {
    return <div>{translate('all.somethingWentWrong.subtitle')}</div>;
  }

  if (isLoading) {
    // TODO Improve skeleton
    return <Skeleton />;
  }

  if (!selectedChat) {
    return <EmptyChat />;
  }

  return (
    <div className={'p-6u w-full flex flex-col flex-1 overflow-hidden'}>
      <div className={'flex gap-4u rounded-9px bg-white pl-6u pr-10u py-6u sticky top-0'}>
        <div className={'flex gap-[0.4688rem] items-center overflow-hidden'}>
          <NotesIcon color={'var(--color-primary)'} />
          <ParagraphWithTooltip
            weight={fontWeights.medium}
            colorClassName={'text-primary'}
            className='text-interface-regular leading-interface-regular'
          >
            {translate('aiChatbot.chat.title', { title: conversation?.title })}
          </ParagraphWithTooltip>
        </div>
        <div className={'flex items-center gap-4u ml-auto'}>
          <Paragraph
            className='text-interface-regular leading-interface-regular whitespace-nowrap'
            colorClassName='text-gray-neutral-base'
          >
            {translate('aiChatbot.chat.activeChat')}
          </Paragraph>
          <ChatOptionsButton />
        </div>
      </div>
      <ChatMessages />
      <LimitReachedWarning className='mt-6u' />
      <div className={'pt-4u'}>
        <ChatInput onSubmit={addMessage} focusOnMount={!isTablet} />
      </div>
    </div>
  );
};

const EmptyChat = () => {
  const translate = useTranslate();
  const { selectedModel, quota, questionsCount } = useAiChatbotContext();
  const { addMessage } = useChatDetailsContext();

  return (
    <div className='flex flex-col p-4u max-w-108 flex-1 overflow-hidden'>
      <div className='flex-1 justify-center items-center text-center overflow-auto scrollbar scrollbar-thumb'>
        <IconWithBackground
          className='bg-white-84 mb-10u mx-auto'
          icon={() => <SearchIconV2 color={'var(--color-primary)'} />}
        />
        <Heading3 weight={fontWeights.semibold} variant='display' variantSize='regular' className='mb-4u'>
          {translate('aiChatbot.newChat.title')}
        </Heading3>
        <Paragraph weight={fontWeights.semibold} variant='interface' variantSize='large' className='mt-3u'>
          {translate('aiChatbot.newChat.exampleQuestions.platform.title')}
        </Paragraph>
        <div className='flex flex-wrap gap-4u mt-4u justify-center w-full overflow-hidden'>
          {Array.from({ length: questionsCount }).map((_, index) => {
            const label = translate(`aiChatbot.newChat.exampleQuestions.platform.question${index + 1}`);

            return (
              <ExampleQuestion
                key={index}
                label={label}
                onClick={addMessage}
                disabled={!selectedModel || !!quota?.limitReached}
                disabledTooltipText={
                  quota?.limitReached
                    ? translate('aiChatbot.limitWarning.tooltip')
                    : translate('aiChatbot.chatInput.noModelSelected')
                }
              />
            );
          })}
        </div>
      </div>

      <div className='mt-4u'>
        {quota && !quota.limitReached && (
          <Paragraph
            weight={fontWeights.medium}
            variant='interface'
            variantSize='regular'
            className='mb-4u text-center'
            colorClassName='text-gray-tundora'
          >
            {translate('aiChatbot.newChat.accuracyWarning')}{' '}
          </Paragraph>
        )}
        <LimitReachedWarning className='my-4u' />
        <ChatInput onSubmit={addMessage} />
      </div>
    </div>
  );
};
